import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ShoppingBag, Trash2, ArrowLeft, ArrowRight, Package, Truck } from "lucide-react";

export default function Cart() {
  const [cart, setCart] = useState([]);

  const loadCart = () => {
    try {
      setCart(JSON.parse(localStorage.getItem("cw_cart") || "[]"));
    } catch {
      setCart([]);
    }
  };

  useEffect(() => {
    loadCart();
    window.addEventListener("cw_cart_updated", loadCart);
    return () => window.removeEventListener("cw_cart_updated", loadCart);
  }, []);

  const saveCart = (newCart) => {
    localStorage.setItem("cw_cart", JSON.stringify(newCart));
    setCart(newCart);
    window.dispatchEvent(new Event("cw_cart_updated"));
  };

  const updateQty = (id, delta) => {
    const updated = cart.map((item) =>
      item.id === id ? { ...item, qty: Math.max(1, (item.qty || 1) + delta) } : item
    );
    saveCart(updated);
  };

  const removeItem = (id) => {
    saveCart(cart.filter((item) => item.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * (item.qty || 1), 0);
  const shipping = subtotal >= 75 ? 0 : 8.99;
  const total = subtotal + shipping;

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 bg-[#f5f3ef] rounded-full flex items-center justify-center mx-auto mb-6">
          <ShoppingBag className="w-8 h-8 text-[#c8a96e]" />
        </div>
        <h1 className="font-display text-3xl font-bold text-[#1a1a1a] mb-3">Your cart is empty</h1>
        <p className="text-gray-500 text-sm mb-8">Looks like you haven't added anything yet. Discover unique handmade items from our creators.</p>
        <Link
          to={createPageUrl("Shop")}
          className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white font-semibold px-8 py-3.5 rounded-full transition-colors"
        >
          Start Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to={createPageUrl("Shop")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#c8a96e] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Continue Shopping
        </Link>
      </div>

      <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8">
        Your Cart <span className="text-gray-400 text-2xl font-normal">({cart.reduce((s, i) => s + (i.qty || 1), 0)} items)</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => (
            <div key={item.id} className="bg-white border border-[#e8e4de] rounded-xl p-4 flex gap-4">
              <Link to={createPageUrl(`ProductDetail?id=${item.id}`)}>
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#f5f3ef] flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={createPageUrl(`ProductDetail?id=${item.id}`)}>
                  <h3 className="font-medium text-[#1a1a1a] text-sm hover:text-[#c8a96e] transition-colors line-clamp-2">{item.name}</h3>
                </Link>
                <p className="text-xs text-[#c8a96e] mt-0.5">{item.creator_name}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-[#e8e4de] rounded-full overflow-hidden">
                    <button
                      onClick={() => updateQty(item.id, -1)}
                      className="px-3 py-1 text-sm hover:bg-[#f5f3ef] transition-colors"
                    >−</button>
                    <span className="px-3 py-1 text-sm font-medium min-w-[2rem] text-center">{item.qty || 1}</span>
                    <button
                      onClick={() => updateQty(item.id, 1)}
                      className="px-3 py-1 text-sm hover:bg-[#f5f3ef] transition-colors"
                    >+</button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-[#1a1a1a]">${(item.price * (item.qty || 1)).toFixed(2)}</span>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-[#e8e4de] rounded-xl p-6 sticky top-24">
            <h2 className="font-display text-xl font-bold text-[#1a1a1a] mb-5">Order Summary</h2>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Shipping</span>
                <span className={`font-medium ${shipping === 0 ? "text-green-600" : ""}`}>
                  {shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              {shipping > 0 && (
                <div className="bg-[#faf9f7] rounded-lg p-3 text-xs text-gray-500 flex items-start gap-2">
                  <Truck className="w-3.5 h-3.5 text-[#c8a96e] flex-shrink-0 mt-0.5" />
                  Add ${(75 - subtotal).toFixed(2)} more for free shipping!
                </div>
              )}
              <div className="border-t border-[#e8e4de] pt-3 flex justify-between">
                <span className="font-semibold text-[#1a1a1a]">Total</span>
                <span className="font-bold text-xl text-[#1a1a1a]">${total.toFixed(2)}</span>
              </div>
            </div>

            <button className="w-full bg-[#1a1a1a] hover:bg-[#c8a96e] text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 text-sm">
              <Package className="w-4 h-4" /> Proceed to Checkout
            </button>

            <p className="text-xs text-gray-400 text-center mt-3">Secure checkout · 100% satisfaction guaranteed</p>

            <div className="mt-5 pt-5 border-t border-[#e8e4de]">
              <p className="text-xs text-gray-400 text-center mb-3">You're supporting</p>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {[...new Set(cart.map((i) => i.creator_name))].map((name) => (
                  <span key={name} className="bg-[#faf9f7] text-[#c8a96e] text-xs px-2 py-0.5 rounded-full font-medium">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}